import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import type { User, League, Round, Submission, Vote, LeagueScoring, CuratedPlaylist } from './mock-data';
import { hashPassword, verifyPassword } from './auth';
import { extractYouTubeId, getYouTubeThumbnail } from './utils';
import * as api from './api';
import { adminAPI } from './api';

interface LeagueMember {
  league_id: string;
  user_id: string;
  is_admin?: boolean;
}

interface AppState {
  version: number;
  currentUser: User | null;
  users: User[];
  leagues: League[];
  rounds: Round[];
  submissions: Submission[];
  votes: Vote[];
  activities: Activity[];
  curatedPlaylists: CuratedPlaylist[];
  league_members: LeagueMember[];
}

interface Activity {
  id: string;
  type: 'vote' | 'submission' | 'result' | 'join' | 'league_created' | 'round_started';
  message: string;
  time: string;
  user_id?: string;
  league_id?: string;
  round_id?: string;
}

const STORAGE_KEY = 'beatbattle_app_state';

const APP_STATE_VERSION = 2;

const defaultState: AppState = {
  version: APP_STATE_VERSION,
  currentUser: null,
  users: [],
  leagues: [],
  rounds: [],
  submissions: [],
  votes: [],
  activities: [],
  curatedPlaylists: [],
  league_members: [],
};

function loadState(): AppState {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      return migrateState(parsed);
    }
  } catch (error) {
    console.error('Failed to load state from localStorage:', error);
  }
  return defaultState;
}

function saveState(state: AppState) {
  try {
    const serialisable = {
      ...state,
      version: APP_STATE_VERSION,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(serialisable));
  } catch (error) {
    console.error('Failed to save state to localStorage:', error);
  }
}

interface StoreContextType {
  state: AppState;
  isLoading: boolean;
  // User actions
  registerUser: (user: { username: string; email: string; password: string; musicleagueUsername?: string; isTransferPage?: boolean }) => Promise<{ user: User; isImportedUser: boolean; alreadyLinked?: boolean }>;
  loginUser: (credentials: { email: string; password: string }) => Promise<User>;
  logout: () => void;
  updateUser: (user: Partial<User>) => void;
  setCurrentUser: (user: User | null) => void;
  
  // League actions
  createLeague: (payload: {
    league: Omit<League, 'id' | 'created_at' | 'member_count' | 'current_round'>;
    rounds: Array<Omit<Round, 'id' | 'league_id' | 'created_at'>>;
  }) => Promise<League>;
  updateLeague: (id: string, updates: Partial<League>) => void;
  joinLeague: (leagueId: string, userId: string) => Promise<void>;
  getLeaguesByUser: (userId: string) => Promise<League[]>;
  
  // Round actions
  createRound: (round: Omit<Round, 'id' | 'created_at'>) => Round;
  updateRound: (id: string, updates: Partial<Round>) => void;
  getRoundsByLeague: (leagueId: string) => Round[];
  
  // Submission actions
  createSubmission: (submission: Omit<Submission, 'id' | 'submitted_at' | 'total_points'>) => Promise<Submission>;
  updateSubmission: (submissionId: string, submission: Partial<Omit<Submission, 'id' | 'submitted_at' | 'total_points'>>) => Promise<Submission>;
  getSubmissionsByRound: (roundId: string) => Submission[];
  getSubmissionByUserAndRound: (userId: string, roundId: string) => Submission | undefined;
  
  // Vote actions
  createVote: (vote: Omit<Vote, 'id' | 'created_at'>) => Vote;
  getVotesByRound: (roundId: string) => Vote[];
  getVotesByUserAndRound: (userId: string, roundId: string) => Vote[];
  hasSubmittedVotes: (userId: string, roundId: string) => boolean;
  calculateRoundResults: (roundId: string) => void;
  
  // Activity actions
  addActivity: (activity: Omit<Activity, 'id' | 'time'>) => void;
  getRecentActivities: (limit?: number) => Activity[];
  
  // Utility
  findLeagueByInviteCode: (code: string) => Promise<League | undefined>;
  generateInviteCode: () => string;
  addSubmissionsToState: (submissions: Submission[]) => void;
  addVotesToState: (votes: Vote[]) => void;
  
  // Admin actions
  resetAllData: () => void;
  resetUsers: () => void;
  resetLeagues: () => void;
  resetRounds: () => void;
  resetSubmissions: () => void;
  resetVotes: () => void;
  resetActivities: () => void;
  setUserAsAdmin: (username: string, isAdmin: boolean) => void;
  
  // Curated playlist actions
  createCuratedPlaylist: (playlist: Omit<CuratedPlaylist, 'id' | 'created_at' | 'updated_at'>) => CuratedPlaylist;
  updateCuratedPlaylist: (id: string, updates: Partial<Omit<CuratedPlaylist, 'id' | 'created_at' | 'created_by'>>) => CuratedPlaylist;
  deleteCuratedPlaylist: (id: string) => void;
  getCuratedPlaylists: () => CuratedPlaylist[];
}

const StoreContext = createContext<StoreContextType | undefined>(undefined);

// Check if Supabase is configured
function isSupabaseConfigured(): boolean {
  const url = import.meta.env.VITE_SUPABASE_URL;
  const key = import.meta.env.VITE_SUPABASE_ANON_KEY;
  return !!(url && key && url !== 'https://placeholder.supabase.co' && key !== 'placeholder-key');
}

// Helper function to check if a string is a valid UUID
function isValidUUID(str: string): boolean {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return uuidRegex.test(str);
}

export function StoreProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AppState>(loadState);
  const [isUsingSupabase, setIsUsingSupabase] = useState(isSupabaseConfigured());
  const [isLoading, setIsLoading] = useState(isUsingSupabase);

  // Load user from Supabase on mount if configured
  useEffect(() => {
    if (isUsingSupabase) {
      // Clear localStorage user data when Supabase is enabled (to avoid ID conflicts)
      const storedUserId = localStorage.getItem('current_user_id');
      if (storedUserId && !isValidUUID(storedUserId)) {
        // If stored user ID is not a UUID, clear it (it's from localStorage mode)
        console.log('[Store] Clearing localStorage user ID (not a UUID):', storedUserId);
        localStorage.removeItem('current_user_id');
        // Also clear the current user from state if it has the old ID format
        setState((prev) => {
          if (prev.currentUser && !isValidUUID(prev.currentUser.id)) {
            return { ...prev, currentUser: null };
          }
          return prev;
        });
      }

      // Try to load user from Supabase
      api.userAPI.getCurrentUser()
        .then((user) => {
          if (user) {
            console.log('[Store] User loaded successfully from Supabase:', user.username);
            setState((prev) => ({
              ...prev,
              currentUser: user,
            }));
            // Ensure user ID is stored in localStorage for persistence
            localStorage.setItem('current_user_id', user.id);
          } else {
            // getCurrentUser returned null - check why
            const storedUserId = localStorage.getItem('current_user_id');
            if (storedUserId && isValidUUID(storedUserId)) {
              // We have a valid user ID but couldn't fetch the user
              // This might be a temporary network issue - try to restore from localStorage state
              const loadedState = loadState();
              if (loadedState.currentUser && loadedState.currentUser.id === storedUserId) {
                console.log('[Store] Restoring user from localStorage state (temporary network issue)');
                setState((prev) => ({
                  ...prev,
                  currentUser: loadedState.currentUser,
                }));
              } else {
                console.log('[Store] getCurrentUser returned null but valid user ID exists. Preserving current user in state if present.');
                // Don't clear the user - keep what we have in state
              }
            } else {
              // No valid user ID - definitely no user
              console.log('[Store] No user found and no valid stored user ID - clearing current user');
              setState((prev) => ({
                ...prev,
                currentUser: null,
              }));
            }
          }
          setIsLoading(false);
        })
        .catch((error) => {
          console.error('[Store] Exception loading user from Supabase:', error);
          // On error, preserve the current user in state if we have a valid user ID
          const storedUserId = localStorage.getItem('current_user_id');
          if (storedUserId && isValidUUID(storedUserId)) {
            console.log('[Store] Error loading user but valid user ID exists - preserving current user in state');
            // Keep the current user in state - don't clear it
          } else {
            // No valid user ID - clear the user
            console.log('[Store] Error loading user and no valid user ID - clearing current user');
            setState((prev) => ({
              ...prev,
              currentUser: null,
            }));
          }
          setIsLoading(false);
        });
    } else {
      setIsLoading(false);
    }
  }, [isUsingSupabase]);

  const updateState = useCallback((updater: (prev: AppState) => AppState) => {
    setState((prev) => {
      const updated = updater(prev);
      // Save to localStorage if not using Supabase, or if we have a current user (for persistence)
      if (!isUsingSupabase) {
        saveState(updated);
      } else if (updated.currentUser) {
        // When using Supabase, save a minimal state with just the current user for persistence
        // This allows the user to stay logged in across refreshes even if Supabase is temporarily unavailable
        try {
          const minimalState = {
            version: APP_STATE_VERSION,
            currentUser: updated.currentUser,
            users: [], // Don't save users array when using Supabase
            leagues: [],
            rounds: [],
            submissions: [],
            votes: [],
            activities: [],
            curatedPlaylists: [],
          };
          localStorage.setItem(STORAGE_KEY, JSON.stringify(minimalState));
        } catch (error) {
          console.error('Failed to save minimal state to localStorage:', error);
        }
      }
      return { ...updated, version: APP_STATE_VERSION };
    });
  }, [isUsingSupabase]);
  
  // Initialize: Set "bruan" as admin if they exist
  useEffect(() => {
    const bruanUser = state.users.find((u) => u.username.toLowerCase() === 'bruan');
    if (bruanUser && !bruanUser.isAdmin) {
      setState((prev) => {
        const updatedUsers = prev.users.map((user) =>
          user.id === bruanUser.id ? { ...user, isAdmin: true } : user
        );
        const updated = {
          ...prev,
          users: updatedUsers,
          currentUser: prev.currentUser?.id === bruanUser.id
            ? { ...prev.currentUser, isAdmin: true }
            : prev.currentUser,
        };
        saveState(updated);
        return { ...updated, version: APP_STATE_VERSION };
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount - intentionally not including state.users to avoid re-running
  
  // Auto-transition round statuses based on deadlines
  const checkRoundDeadlines = useCallback(() => {
    const now = new Date();
    updateState((prev) => {
      const updatedRounds = prev.rounds.map((round) => {
        if (round.status === 'submitting') {
          const submissionDeadline = new Date(round.submission_deadline);
          if (now > submissionDeadline) {
            return { ...round, status: 'voting' as const };
          }
        } else if (round.status === 'voting') {
          const votingDeadline = new Date(round.voting_deadline);
          if (now > votingDeadline) {
            return { ...round, status: 'completed' as const };
          }
        }
        return round;
      });
      
      // Update leagues with updated rounds
      const updatedLeagues = prev.leagues.map((league) => {
        if (league.current_round) {
          const updatedRound = updatedRounds.find((r) => r.id === league.current_round?.id);
          if (updatedRound && updatedRound.status !== league.current_round.status) {
            return { ...league, current_round: updatedRound };
          }
        }
        return league;
      });
      
      return {
        ...prev,
        rounds: updatedRounds,
        leagues: updatedLeagues,
      };
    });
  }, [updateState]);

  // Auto-save to localStorage whenever state changes (only if not using Supabase)
  useEffect(() => {
    if (!isUsingSupabase) {
      saveState(state);
    }
  }, [state, isUsingSupabase]);
  
  // Check round deadlines periodically
  useEffect(() => {
    const interval = setInterval(() => {
      checkRoundDeadlines();
    }, 60000); // Check every minute
    
    // Check immediately
    checkRoundDeadlines();
    
    return () => clearInterval(interval);
  }, [checkRoundDeadlines]);

  // User actions
  const getAuthenticatedUser = () => {
    if (!state.currentUser) {
      throw new Error('User must be authenticated to perform this action.');
    }
    return state.currentUser;
  };

  const registerUser = async ({
    username,
    email,
    password,
    musicleagueUsername,
    isTransferPage,
  }: {
    username: string;
    email: string;
    password: string;
    musicleagueUsername?: string;
    isTransferPage?: boolean;
  }) => {
    // Use Supabase API if configured
    if (isUsingSupabase) {
      try {
        const result = await api.userAPI.register({ username, email, password, musicleagueUsername, isTransferPage });
        setState((prev) => ({
          ...prev,
          currentUser: result.user,
        }));
        // Store user ID for session
        localStorage.setItem('current_user_id', result.user.id);
        return result;
      } catch (error) {
        throw error;
      }
    }

    // Fallback to localStorage
    const trimmedUsername = username.trim();
    const normalizedEmail = email.trim().toLowerCase();

    if (!trimmedUsername) {
      throw new Error('Username is required.');
    }

    if (!normalizedEmail) {
      throw new Error('Email is required.');
    }

    const emailExists = state.users.some(
      (user) => user.email.toLowerCase() === normalizedEmail
    );

    if (emailExists) {
      throw new Error('An account with this email already exists.');
    }

    const passwordHash = await hashPassword(password);
    const newUser: User = {
      id: `user_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
      username: trimmedUsername,
      email: normalizedEmail,
      total_leagues: 0,
      total_wins: 0,
      total_submissions: 0,
      passwordHash,
    };

    updateState((prev) => ({
      ...prev,
      users: [...prev.users, newUser],
      currentUser: newUser,
    }));

    return { user: newUser, isImportedUser: false };
  };

  const loginUser = async ({
    email,
    password,
  }: {
    email: string;
    password: string;
  }) => {
    // Use Supabase API if configured
    if (isUsingSupabase) {
      try {
        const user = await api.userAPI.login({ email, password });
        setState((prev) => ({
          ...prev,
          currentUser: user,
        }));
        // Store user ID for session
        localStorage.setItem('current_user_id', user.id);
        return user;
      } catch (error) {
        throw error;
      }
    }

    // Fallback to localStorage
    const normalizedInput = email.trim().toLowerCase();
    const isEmail = normalizedInput.includes('@');
    
    // Try email first, then username
    let user = state.users.find(
      (u) => u.email.toLowerCase() === normalizedInput
    );
    
    if (!user && !isEmail) {
      // Try username if email lookup failed and input doesn't look like email
      user = state.users.find(
        (u) => u.username.toLowerCase() === normalizedInput
      );
    }

    if (!user || !user.passwordHash) {
      throw new Error('Invalid email/username or password.');
    }

    const isValid = await verifyPassword(password, user.passwordHash);

    if (!isValid) {
      throw new Error('Invalid email or password.');
    }

    updateState((prev) => ({
      ...prev,
      currentUser: user,
    }));

    return user;
  };

  const logout = () => {
    if (isUsingSupabase) {
      localStorage.removeItem('current_user_id');
    }
    updateState((prev) => ({
      ...prev,
      currentUser: null,
    }));
  };

  const updateUser = (updates: Partial<User>) => {
    const authUser = getAuthenticatedUser();

    const sanitizedEmail = updates.email?.trim();
    if (sanitizedEmail) {
      const emailExists = state.users.some(
        (user) =>
          user.email.toLowerCase() === sanitizedEmail.toLowerCase() &&
          user.id !== authUser.id
      );

      if (emailExists) {
        throw new Error('Another account is already using this email.');
      }
    }

    const { passwordHash, id, ...restUpdates } = updates;
    const normalizedUpdates = {
      ...restUpdates,
      ...(sanitizedEmail ? { email: sanitizedEmail.toLowerCase() } : {}),
    };

    const updatedUser: User = {
      ...authUser,
      ...normalizedUpdates,
    };

    updateState((prev) => ({
      ...prev,
      currentUser: updatedUser,
      users: prev.users.map((user) =>
        user.id === updatedUser.id
          ? { ...user, ...normalizedUpdates }
          : user
      ),
    }));
  };

  const setCurrentUser = (user: User | null) => {
    updateState((prev) => ({ ...prev, currentUser: user }));
  };

  // League actions
  const createLeague = async ({
    league: leagueData,
    rounds: roundsInput,
  }: {
    league: Omit<League, 'id' | 'created_at' | 'member_count' | 'current_round'>;
    rounds: Array<Omit<Round, 'id' | 'league_id' | 'created_at'>>;
  }): Promise<League> => {
    const authUser = getAuthenticatedUser();

    if (!roundsInput.length) {
      throw new Error('At least one round is required to create a league.');
    }

    // Use Supabase API if configured
    if (isUsingSupabase) {
      try {
        const newLeague = await api.leagueAPI.createLeague({
          league: leagueData,
          rounds: roundsInput,
        });
        // Update local state
        setState((prev) => ({
          ...prev,
          leagues: [...prev.leagues, newLeague],
        }));
        return newLeague;
      } catch (error) {
        throw error;
      }
    }

    // Fallback to localStorage
    let newLeague: League;
    let createdRounds: Round[] = [];

    updateState((prev) => {
      const leagueId = `league_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      const timestamp = new Date().toISOString();

      createdRounds = roundsInput.map((round, index) => ({
        ...round,
        league_id: leagueId,
        id: `round_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        round_number: typeof round.round_number === 'number' ? round.round_number : index + 1,
        status: round.status ?? (index === 0 ? 'submitting' : 'submitting'),
        created_at: timestamp,
      }));

      newLeague = {
        ...leagueData,
        id: leagueId,
        invite_code: leagueData.invite_code.toUpperCase(), // Ensure invite code is uppercase
        created_at: timestamp,
        member_count: 1,
        current_round: createdRounds[0],
      };

      return {
        ...prev,
        leagues: [...prev.leagues, newLeague],
        rounds: [...prev.rounds, ...createdRounds],
      };
    });

    addActivity({
      type: 'league_created',
      message: `You created "${newLeague.name}"`,
      user_id: authUser.id,
      league_id: newLeague.id,
    });

    return newLeague;
  };

  const updateLeague = (id: string, updates: Partial<League>) => {
    updateState((prev) => {
      const existingIndex = prev.leagues.findIndex((l) => l.id === id);
      if (existingIndex >= 0) {
        // Update existing league
        return {
          ...prev,
          leagues: prev.leagues.map((l) => (l.id === id ? { ...l, ...updates } : l)),
        };
      } else {
        // Add new league if it doesn't exist (for Supabase fetched leagues)
        const newLeague = updates as League;
        return {
          ...prev,
          leagues: [...prev.leagues, newLeague],
        };
      }
    });
  };

  const joinLeague = async (leagueId: string, userId: string) => {
    const authUser = getAuthenticatedUser();

    // Use Supabase API if configured
    if (isUsingSupabase) {
      try {
        await api.leagueAPI.joinLeague(leagueId, userId);
        // Refresh league data
        const updatedLeague = await api.leagueAPI.getLeague(leagueId);
        if (updatedLeague) {
          setState((prev) => ({
            ...prev,
            leagues: prev.leagues.map((l) => (l.id === leagueId ? updatedLeague : l)),
          }));
        }
        return;
      } catch (error) {
        throw error;
      }
    }

    // Fallback to localStorage
    updateState((prev) => {
      const league = prev.leagues.find((l) => l.id === leagueId);
      if (!league || league.member_count >= league.max_players) {
        return prev;
      }

      const user = prev.users.find((u) => u.id === userId);
      return {
        ...prev,
        leagues: prev.leagues.map((l) =>
          l.id === leagueId ? { ...l, member_count: l.member_count + 1 } : l
        ),
      };
    });

    const league = state.leagues.find((l) => l.id === leagueId);
    if (league) {
      addActivity({
        type: 'join',
        message: `${authUser.username} joined "${league.name}"`,
        user_id: userId,
        league_id: leagueId,
      });
    }
  };

  const getLeaguesByUser = async (userId: string): Promise<League[]> => {
    // Use Supabase API if configured
    if (isUsingSupabase) {
      // Validate that the user ID is a UUID (Supabase uses UUIDs)
      if (!isValidUUID(userId)) {
        console.error('Invalid user ID format for Supabase (not a UUID):', userId);
        // Return empty array if user ID is not a UUID
        return [];
      }

      try {
        const leagues = await api.leagueAPI.getLeaguesByUser(userId);
        // Update local state
        setState((prev) => ({
          ...prev,
          leagues: leagues,
        }));
        return leagues;
      } catch (error) {
        console.error('Failed to get leagues:', error);
        return [];
      }
    }

    // Fallback to localStorage
    return state.leagues.filter((league) => 
      league.created_by === userId || 
      state.league_members.some((lm) => lm.league_id === league.id && lm.user_id === userId)
    );
  };

  // Round actions
  const createRound = (roundData: Omit<Round, 'id' | 'created_at'>): Round => {
    const newRound: Round = {
      ...roundData,
      id: `round_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      created_at: new Date().toISOString(),
    };

    updateState((prev) => ({
      ...prev,
      rounds: [...prev.rounds, newRound],
    }));

    return newRound;
  };

  const updateRound = (id: string, updates: Partial<Round>) => {
    updateState((prev) => {
      const existingIndex = prev.rounds.findIndex((r) => r.id === id);
      if (existingIndex >= 0) {
        // Update existing round
        return {
          ...prev,
          rounds: prev.rounds.map((r) => (r.id === id ? { ...r, ...updates } : r)),
          // Update current_round in leagues if this round is current
          leagues: prev.leagues.map((l) =>
            l.current_round?.id === id
              ? { ...l, current_round: { ...l.current_round, ...updates } }
              : l
          ),
        };
      } else {
        // Add new round if it doesn't exist (for Supabase fetched rounds)
        const newRound = updates as Round;
        return {
          ...prev,
          rounds: [...prev.rounds, newRound],
          // Update current_round in leagues if this round should be current
          leagues: prev.leagues.map((l) =>
            l.id === newRound.league_id && (!l.current_round || l.current_round.id === id)
              ? { ...l, current_round: newRound }
              : l
          ),
        };
      }
    });
  };

  const getRoundsByLeague = (leagueId: string): Round[] => {
    return state.rounds.filter((r) => r.league_id === leagueId);
  };

  // Submission actions
  const createSubmission = async (
    submissionData: Omit<Submission, 'id' | 'submitted_at' | 'total_points'>
  ): Promise<Submission> => {
    const youtubeId = submissionData.video_url ? extractYouTubeId(submissionData.video_url) : undefined;
    
    // Use Supabase API if configured
    if (isUsingSupabase) {
      try {
        const newSubmission = await api.submissionAPI.createSubmission({
          ...submissionData,
          video_thumbnail:
            submissionData.video_thumbnail ??
            (youtubeId ? getYouTubeThumbnail(youtubeId, 'mqdefault') : submissionData.spotify_album_image),
        });
        
        // Update local state
        updateState((prev) => ({
          ...prev,
          submissions: [...prev.submissions, newSubmission],
        }));

        const round = state.rounds.find((r) => r.id === submissionData.round_id);
        const league = state.leagues.find((l) => 
          l.id === round?.league_id || state.rounds.some(r => r.id === submissionData.round_id && r.league_id === l.id)
        );

        addActivity({
          type: 'submission',
          message: `You submitted "${newSubmission.song_title}" by ${newSubmission.artist_name} to "${league?.name || 'league'}"`,
          user_id: submissionData.user_id,
          round_id: submissionData.round_id,
          league_id: league?.id,
        });

        return newSubmission;
      } catch (error) {
        throw error;
      }
    }

    // Fallback to localStorage
    const newSubmission: Submission = {
      ...submissionData,
      id: `sub_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      submitted_at: new Date().toISOString(),
      total_points: 0,
      video_thumbnail:
        submissionData.video_thumbnail ??
        (youtubeId ? getYouTubeThumbnail(youtubeId, 'mqdefault') : submissionData.spotify_album_image),
    };

    updateState((prev) => ({
      ...prev,
      submissions: [...prev.submissions, newSubmission],
    }));

    const round = state.rounds.find((r) => r.id === submissionData.round_id);
    const league = state.leagues.find((l) => 
      l.id === round?.league_id || state.rounds.some(r => r.id === submissionData.round_id && r.league_id === l.id)
    );

    addActivity({
      type: 'submission',
      message: `You submitted "${newSubmission.song_title}" by ${newSubmission.artist_name} to "${league?.name || 'league'}"`,
      user_id: submissionData.user_id,
      round_id: submissionData.round_id,
      league_id: league?.id,
    });

    return newSubmission;
  };

  const getSubmissionsByRound = (roundId: string): Submission[] => {
    return state.submissions.filter((s) => s.round_id === roundId);
  };

  const getSubmissionByUserAndRound = (
    userId: string,
    roundId: string
  ): Submission | undefined => {
    return state.submissions.find(
      (s) => s.user_id === userId && s.round_id === roundId
    );
  };

  const addSubmissionsToState = (newSubmissions: Submission[]) => {
    updateState((prev) => {
      const existingIds = new Set(prev.submissions.map((s) => s.id));
      const submissionsToAdd = newSubmissions.filter((s) => !existingIds.has(s.id));
      const updatedSubmissions = [...prev.submissions];
      
      // Update existing submissions with latest data
      newSubmissions.forEach((sub) => {
        const index = updatedSubmissions.findIndex((s) => s.id === sub.id);
        if (index >= 0) {
          updatedSubmissions[index] = sub;
        }
      });
      
      return {
        ...prev,
        submissions: [...updatedSubmissions, ...submissionsToAdd],
      };
    });
  };

  const addVotesToState = (newVotes: Vote[]) => {
    updateState((prev) => {
      const existingIds = new Set(prev.votes.map((v) => v.id));
      const votesToAdd = newVotes.filter((v) => !existingIds.has(v.id));
      const updatedVotes = [...prev.votes];
      
      // Update existing votes with latest data (important for is_submitted status)
      newVotes.forEach((vote) => {
        const index = updatedVotes.findIndex((v) => 
          v.voter_id === vote.voter_id && 
          v.submission_id === vote.submission_id && 
          v.round_id === vote.round_id
        );
        if (index >= 0) {
          updatedVotes[index] = vote; // Update with latest vote data
        }
      });
      
      return {
        ...prev,
        votes: [...updatedVotes, ...votesToAdd],
      };
    });
  };

  const updateSubmission = async (
    submissionId: string,
    submissionData: Partial<Omit<Submission, 'id' | 'submitted_at' | 'total_points'>>
  ): Promise<Submission> => {
    const existingSubmission = state.submissions.find((s) => s.id === submissionId);
    if (!existingSubmission) {
      throw new Error('Submission not found');
    }

    const youtubeId = submissionData.video_url
      ? extractYouTubeId(submissionData.video_url)
      : existingSubmission.youtube_video_id;

    // Use Supabase API if configured
    if (isUsingSupabase) {
      try {
        // video_thumbnail is not stored in DB - it's computed from youtube_video_id or spotify_album_image
        // So we don't pass it to the API, just update the fields that exist in the database
        const updatedSubmission = await api.submissionAPI.updateSubmission(submissionId, submissionData);

        // Update local state - compute video_thumbnail from youtube_video_id if needed
        const submissionWithThumbnail = {
          ...updatedSubmission,
          video_thumbnail:
            updatedSubmission.video_thumbnail ??
            (updatedSubmission.youtube_video_id
              ? getYouTubeThumbnail(updatedSubmission.youtube_video_id, 'mqdefault')
              : undefined),
        };
        
        updateState((prev) => ({
          ...prev,
          submissions: prev.submissions.map((s) =>
            s.id === submissionId ? submissionWithThumbnail : s
          ),
        }));

        const round = state.rounds.find((r) => r.id === updatedSubmission.round_id);
        const league = state.leagues.find((l) =>
          l.id === round?.league_id || state.rounds.some(r => r.id === updatedSubmission.round_id && r.league_id === l.id)
        );

        addActivity({
          type: 'submission',
          message: `You updated your submission "${updatedSubmission.song_title}" by ${updatedSubmission.artist_name} in "${league?.name || 'league'}"`,
          user_id: updatedSubmission.user_id,
          round_id: updatedSubmission.round_id,
          league_id: league?.id,
        });

        return updatedSubmission;
      } catch (error) {
        throw error;
      }
    }

    // Fallback to localStorage
    const updatedSubmission: Submission = {
      ...existingSubmission,
      ...submissionData,
      video_thumbnail:
        submissionData.video_thumbnail ??
        existingSubmission.video_thumbnail ??
        (youtubeId ? getYouTubeThumbnail(youtubeId, 'mqdefault') : undefined),
    };

    updateState((prev) => ({
      ...prev,
      submissions: prev.submissions.map((s) =>
        s.id === submissionId ? updatedSubmission : s
      ),
    }));

    const round = state.rounds.find((r) => r.id === updatedSubmission.round_id);
    const league = state.leagues.find((l) =>
      l.id === round?.league_id || state.rounds.some(r => r.id === updatedSubmission.round_id && r.league_id === l.id)
    );

    addActivity({
      type: 'submission',
      message: `You updated your submission "${updatedSubmission.song_title}" by ${updatedSubmission.artist_name} in "${league?.name || 'league'}"`,
      user_id: updatedSubmission.user_id,
      round_id: updatedSubmission.round_id,
      league_id: league?.id,
    });

    return updatedSubmission;
  };

  // Vote actions
  const createVote = (voteData: Omit<Vote, 'id' | 'created_at'>): Vote => {
    const newVote: Vote = {
      ...voteData,
      id: `vote_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      created_at: new Date().toISOString(),
    };

    updateState((prev) => {
      // Remove any existing vote from this user for this submission
      const filteredVotes = prev.votes.filter(
        (v) => !(v.voter_id === voteData.voter_id && v.submission_id === voteData.submission_id)
      );

      return {
        ...prev,
        votes: [...filteredVotes, newVote],
      };
    });

    // Recalculate results
    calculateRoundResults(voteData.round_id);

    return newVote;
  };

  const getVotesByRound = (roundId: string): Vote[] => {
    return state.votes.filter((v) => v.round_id === roundId);
  };

  const getVotesByUserAndRound = (userId: string, roundId: string): Vote[] => {
    return state.votes.filter((v) => v.voter_id === userId && v.round_id === roundId);
  };

  const hasSubmittedVotes = (userId: string, roundId: string): boolean => {
    const userVotes = getVotesByUserAndRound(userId, roundId);
    if (userVotes.length === 0) return false;
    // All votes must be submitted (is_submitted === true)
    return userVotes.every(v => v.is_submitted === true);
  };

  const calculateRoundResults = (roundId: string) => {
    updateState((prev) => {
      // Only count submitted votes (is_submitted === true), not drafts
      const roundVotes = prev.votes.filter((v) => v.round_id === roundId && v.is_submitted === true);
      
      // Calculate total points for each submission
      const submissionPoints = new Map<string, number>();
      roundVotes.forEach((vote) => {
        const current = submissionPoints.get(vote.submission_id) || 0;
        submissionPoints.set(vote.submission_id, current + vote.points);
      });

      // Update submissions with new totals
      const updatedSubmissions = prev.submissions.map((sub) => {
        if (sub.round_id === roundId) {
          return {
            ...sub,
            total_points: submissionPoints.get(sub.id) || 0,
          };
        }
        return sub;
      });

      return {
        ...prev,
        submissions: updatedSubmissions,
      };
    });
  };

  // Activity actions
  const addActivity = (activityData: Omit<Activity, 'id' | 'time'>) => {
    const now = new Date();
    const timeAgo = formatTimeAgo(now);
    
    const newActivity: Activity = {
      ...activityData,
      id: `act_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      time: timeAgo,
    };

    updateState((prev) => ({
      ...prev,
      activities: [newActivity, ...prev.activities].slice(0, 50), // Keep last 50
    }));
  };

  const getRecentActivities = (limit = 10): Activity[] => {
    return state.activities.slice(0, limit);
  };

  // Utility functions
  const findLeagueByInviteCode = async (code: string): Promise<League | undefined> => {
    // Use Supabase API if configured
    if (isUsingSupabase) {
      try {
        const league = await api.leagueAPI.findLeagueByInviteCode(code);
        if (league) {
          // Update local state
          setState((prev) => {
            const existingIndex = prev.leagues.findIndex((l) => l.id === league.id);
            if (existingIndex >= 0) {
              const updatedLeagues = [...prev.leagues];
              updatedLeagues[existingIndex] = league;
              return { ...prev, leagues: updatedLeagues };
            } else {
              return { ...prev, leagues: [...prev.leagues, league] };
            }
          });
        }
        return league || undefined;
      } catch (error) {
        console.error('Failed to find league by invite code:', error);
        return undefined;
      }
    }

    // Fallback to localStorage
    const normalizedCode = code.toUpperCase().trim();
    return state.leagues.find((l) => l.invite_code.toUpperCase().trim() === normalizedCode);
  };

  const generateInviteCode = (): string => {
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let result = '';
    for (let i = 0; i < 6; i++) {
      result += characters.charAt(Math.floor(Math.random() * characters.length));
    }
    // Ensure result is uppercase
    result = result.toUpperCase();
    
    // Ensure uniqueness (case-insensitive check)
    if (state.leagues.some((l) => l.invite_code.toUpperCase() === result)) {
      return generateInviteCode();
    }
    
    return result;
  };

  // Admin reset functions
  const resetAllData = async () => {
    if (isUsingSupabase) {
      try {
        await adminAPI.resetAllData();
        // Clear local state
        updateState((prev) => ({
          ...prev,
          users: [],
          leagues: [],
          rounds: [],
          submissions: [],
          votes: [],
          activities: [],
          currentUser: null,
        }));
        localStorage.removeItem('current_user_id');
      } catch (error) {
        console.error('Failed to reset all data:', error);
        throw error;
      }
    } else {
      updateState((prev) => ({
        ...prev,
        users: [],
        leagues: [],
        rounds: [],
        submissions: [],
        votes: [],
        activities: [],
        currentUser: null,
      }));
      localStorage.removeItem(STORAGE_KEY);
    }
  };

  const resetUsers = async () => {
    if (isUsingSupabase) {
      try {
        await adminAPI.resetUsers();
        updateState((prev) => ({
          ...prev,
          users: [],
          currentUser: null,
        }));
        localStorage.removeItem('current_user_id');
      } catch (error) {
        console.error('Failed to reset users:', error);
        throw error;
      }
    } else {
      updateState((prev) => ({
        ...prev,
        users: [],
        currentUser: null,
      }));
    }
  };

  const resetLeagues = async () => {
    if (isUsingSupabase) {
      try {
        await adminAPI.resetLeagues();
        updateState((prev) => ({
          ...prev,
          leagues: [],
          rounds: [],
          submissions: [],
          votes: [],
          activities: prev.activities.filter(a => a.type !== 'league_created' && a.type !== 'round_started'),
        }));
      } catch (error) {
        console.error('Failed to reset leagues:', error);
        throw error;
      }
    } else {
      updateState((prev) => ({
        ...prev,
        leagues: [],
        rounds: [],
        submissions: [],
        votes: [],
        activities: prev.activities.filter(a => a.type !== 'league_created' && a.type !== 'round_started'),
      }));
    }
  };

  const resetRounds = async () => {
    if (isUsingSupabase) {
      try {
        await adminAPI.resetRounds();
        updateState((prev) => ({
          ...prev,
          rounds: [],
          submissions: [],
          votes: [],
          activities: prev.activities.filter(a => a.type !== 'round_started'),
        }));
      } catch (error) {
        console.error('Failed to reset rounds:', error);
        throw error;
      }
    } else {
      updateState((prev) => ({
        ...prev,
        rounds: [],
        submissions: [],
        votes: [],
        activities: prev.activities.filter(a => a.type !== 'round_started'),
      }));
    }
  };

  const resetSubmissions = async () => {
    if (isUsingSupabase) {
      try {
        await adminAPI.resetSubmissions();
        updateState((prev) => ({
          ...prev,
          submissions: [],
          votes: [],
          activities: prev.activities.filter(a => a.type !== 'submission'),
        }));
      } catch (error) {
        console.error('Failed to reset submissions:', error);
        throw error;
      }
    } else {
      updateState((prev) => ({
        ...prev,
        submissions: [],
        votes: [],
        activities: prev.activities.filter(a => a.type !== 'submission'),
      }));
    }
  };

  const resetVotes = async () => {
    if (isUsingSupabase) {
      try {
        await adminAPI.resetVotes();
        updateState((prev) => ({
          ...prev,
          votes: [],
          activities: prev.activities.filter(a => a.type !== 'vote'),
          // Reset submission points
          submissions: prev.submissions.map(s => ({ ...s, total_points: 0 })),
        }));
      } catch (error) {
        console.error('Failed to reset votes:', error);
        throw error;
      }
    } else {
      updateState((prev) => ({
        ...prev,
        votes: [],
        activities: prev.activities.filter(a => a.type !== 'vote'),
        // Reset submission points
        submissions: prev.submissions.map(s => ({ ...s, total_points: 0 })),
      }));
    }
  };

  const resetActivities = async () => {
    if (isUsingSupabase) {
      try {
        await adminAPI.resetActivities();
        updateState((prev) => ({
          ...prev,
          activities: [],
        }));
      } catch (error) {
        console.error('Failed to reset activities:', error);
        throw error;
      }
    } else {
      updateState((prev) => ({
        ...prev,
        activities: [],
      }));
    }
  };

  const setUserAsAdmin = (username: string, isAdmin: boolean) => {
    updateState((prev) => {
      const userToUpdate = prev.users.find((u) => u.username.toLowerCase() === username.toLowerCase());
      if (!userToUpdate) {
        console.warn(`User "${username}" not found`);
        return prev;
      }

      const updatedUsers = prev.users.map((user) =>
        user.id === userToUpdate.id ? { ...user, isAdmin } : user
      );

      return {
        ...prev,
        users: updatedUsers,
        // Also update currentUser if it's the same user
        currentUser: prev.currentUser?.id === userToUpdate.id
          ? { ...prev.currentUser, isAdmin }
          : prev.currentUser,
      };
    });
  };

  // Curated playlist functions
  const createCuratedPlaylist = (playlistData: Omit<CuratedPlaylist, 'id' | 'created_at' | 'updated_at'>): CuratedPlaylist => {
    const now = new Date().toISOString();
    const newPlaylist: CuratedPlaylist = {
      ...playlistData,
      id: `curated_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      created_at: now,
      updated_at: now,
    };

    updateState((prev) => ({
      ...prev,
      curatedPlaylists: [...prev.curatedPlaylists, newPlaylist],
    }));

    return newPlaylist;
  };

  const updateCuratedPlaylist = (id: string, updates: Partial<Omit<CuratedPlaylist, 'id' | 'created_at' | 'created_by'>>): CuratedPlaylist => {
    const existing = state.curatedPlaylists.find((p) => p.id === id);
    if (!existing) {
      throw new Error('Curated playlist not found');
    }

    const updated: CuratedPlaylist = {
      ...existing,
      ...updates,
      updated_at: new Date().toISOString(),
    };

    updateState((prev) => ({
      ...prev,
      curatedPlaylists: prev.curatedPlaylists.map((p) => (p.id === id ? updated : p)),
    }));

    return updated;
  };

  const deleteCuratedPlaylist = (id: string) => {
    updateState((prev) => ({
      ...prev,
      curatedPlaylists: prev.curatedPlaylists.filter((p) => p.id !== id),
    }));
  };

  const getCuratedPlaylists = (): CuratedPlaylist[] => {
    return state.curatedPlaylists;
  };

  const value: StoreContextType = {
    state,
    isLoading,
    registerUser,
    loginUser,
    logout,
    updateUser,
    setCurrentUser,
    createLeague,
    updateLeague,
    joinLeague,
    getLeaguesByUser,
    createRound,
    updateRound,
    getRoundsByLeague,
    createSubmission,
    updateSubmission,
    getSubmissionsByRound,
    getSubmissionByUserAndRound,
    createVote,
    getVotesByRound,
    getVotesByUserAndRound,
    hasSubmittedVotes,
    calculateRoundResults,
    addActivity,
    getRecentActivities,
    findLeagueByInviteCode,
    generateInviteCode,
    addSubmissionsToState,
    addVotesToState,
    resetAllData,
    resetUsers,
    resetLeagues,
    resetRounds,
    resetSubmissions,
    resetVotes,
    resetActivities,
    setUserAsAdmin,
    createCuratedPlaylist,
    updateCuratedPlaylist,
    deleteCuratedPlaylist,
    getCuratedPlaylists,
  };

  return <StoreContext.Provider value={value}>{children}</StoreContext.Provider>;
}

export function useStore() {
  const context = useContext(StoreContext);
  if (!context) {
    throw new Error('useStore must be used within StoreProvider');
  }
  return context;
}

function formatTimeAgo(date: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
  return date.toLocaleDateString();
}

function migrateState(raw: Partial<AppState> & { version?: number }): AppState {
  if (!raw) {
    return defaultState;
  }

  const previousVersion = typeof raw.version === 'number' ? raw.version : 1;
  let merged: AppState = {
    ...defaultState,
    ...raw,
    version: APP_STATE_VERSION,
  };

  if (previousVersion < 2) {
    merged = {
      ...merged,
      currentUser: isCustomRecord(raw.currentUser) ? raw.currentUser : null,
      users: stripLegacyRecords(raw.users),
      leagues: stripLegacyRecords(raw.leagues).map(upgradeLegacyLeague),
      rounds: stripLegacyRecords(raw.rounds),
      submissions: stripLegacyRecords(raw.submissions),
      votes: stripLegacyRecords(raw.votes),
      activities: [],
      curatedPlaylists: [],
    };
  }

  // Ensure curatedPlaylists exists for older versions
  if (!merged.curatedPlaylists) {
    merged.curatedPlaylists = [];
  }

  return merged;
}

function upgradeLegacyLeague(league: League): League {
  if (league.scoring) {
    return {
      ...league,
      require_spotify: league.require_spotify ?? false,
    };
  }

  const fallbackUpvotes = league.points_per_round || 10;
  const maxUpvotesPerSong = Math.max(1, Math.min(5, Math.round(fallbackUpvotes / 4)));

  return {
    ...league,
    scoring: {
      max_upvotes_per_player: fallbackUpvotes,
      max_upvotes_per_song: maxUpvotesPerSong,
      max_downvotes_per_player: 0,
      max_downvotes_per_song: 0,
    },
    require_spotify: league.require_spotify ?? false,
  };
}

function isCustomRecord<T extends { id: string } | null | undefined>(record: T): record is Exclude<T, null | undefined> {
  return Boolean(record && typeof record.id === 'string' && record.id.includes('_'));
}

function stripLegacyRecords<T extends { id: string }>(records?: T[]): T[] {
  if (!Array.isArray(records)) {
    return [];
  }

  return records.filter((record) => isCustomRecord(record));
}
